// src/context/AppNotificationsContext.jsx
//
// Tracks the logged-in user's in-app notifications (the bell / /notifications
// module) globally, so the unread badge (sidebar + header bell) and the
// "new notification" popup+sound work the same way from any page/module.
//
// - Polls the backend every 60 seconds for the current notification list.
// - Unread count drives the badge on the sidebar "Notifications" item and
//   the header bell (like WhatsApp's unread chat badge).
// - Any notification that is unread AND wasn't seen on a previous poll is
//   treated as "just arrived" -> shows a 3-second toast (via the existing
//   toast system) + a short chime, no matter which page the user is on.
// - Marking as read / deleting / clearing updates the badge immediately,
//   everywhere, without waiting for the next poll.

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';

const AppNotificationsContext = createContext(null);

const POLL_INTERVAL_MS = 60 * 1000; // Bug/feature request: check every 1 minute

// Short two-tone chime generated with the Web Audio API — no external
// audio file needed. Fails silently if the browser blocks autoplay audio.
const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1180, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.16, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
    setTimeout(() => ctx.close(), 500);
  } catch (e) {
    // Audio not available - not critical, badge/toast still work
  }
};

const extractList = (res) => {
  if (Array.isArray(res)) return res;
  return res?.data || res?.notifications || [];
};

export const AppNotificationsProvider = ({ children }) => {
  const { user } = useAuth();
  const { notify } = useNotification();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const pollRef = useRef(null);

  const fetchAndDiff = useCallback(async () => {
    try {
      const res = await api.get('/api/notifications');
      const list = extractList(res);
      setNotifications(list);

      const unread = list.filter((n) => !n.is_read);
      setUnreadCount(unread.length);

      if (!initializedRef.current) {
        // First load (e.g. right after login) - just record what's already
        // there. The badge count shows immediately, but we don't burst-popup
        // a whole backlog of old unread notifications.
        list.forEach((n) => seenIdsRef.current.add(n.id));
        initializedRef.current = true;
        return;
      }

      const freshOnes = unread.filter((n) => !seenIdsRef.current.has(n.id));
      list.forEach((n) => seenIdsRef.current.add(n.id));

      if (freshOnes.length > 0) {
        playChime();
        // Cap to 3 popups at once so a burst of notifications doesn't flood the screen
        freshOnes.slice(0, 3).forEach((n) => {
          notify((n.title ? n.title + ': ' : '') + n.message, 'info', 3000);
        });
      }
    } catch (err) {
      // Silent fail on poll - don't interrupt the user's work over a network blip
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notify]);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      seenIdsRef.current = new Set();
      initializedRef.current = false;
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchAndDiff();
    pollRef.current = setInterval(fetchAndDiff, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const markAsRead = useCallback(async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await api.put('/api/notifications/' + id + '/read');
    } catch (err) {
      // Resync from server if the request failed, so the badge doesn't lie
      fetchAndDiff();
    }
  }, [fetchAndDiff]);

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    try {
      await api.put('/api/notifications/read-all');
    } catch (err) {
      fetchAndDiff();
    }
  }, [fetchAndDiff]);

  const removeNotification = useCallback(async (id) => {
    let wasUnread = false;
    setNotifications((prev) => {
      const target = prev.find((n) => n.id === id);
      wasUnread = target && !target.is_read;
      return prev.filter((n) => n.id !== id);
    });
    if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await api.delete('/api/notifications/' + id);
    } catch (err) {
      fetchAndDiff();
    }
  }, [fetchAndDiff]);

  const clearAll = useCallback(async () => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      await api.delete('/api/notifications/clear-all');
    } catch (err) {
      fetchAndDiff();
    }
  }, [fetchAndDiff]);

  return (
    <AppNotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        refresh: fetchAndDiff,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAll,
      }}
    >
      {children}
    </AppNotificationsContext.Provider>
  );
};

export const useAppNotifications = () => {
  const ctx = useContext(AppNotificationsContext);
  if (!ctx) throw new Error('useAppNotifications must be used within AppNotificationsProvider');
  return ctx;
};